import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography
} from "@mui/material";
import { TableColumnsPicker } from "components/Documents/Report-sections-forms/TableColumnsPicker";
import {
  AutocompleteMultiSelect,
  SelectOption
} from "components/reports/AutocompleteMultiSelect/AutocompleteMultiSelect";
import {
  ColumnSettingsPopover,
  ColumnSettingsPopoverProps
} from "components/reports/ReportMeasuresTable/ColumnSettingsPopover";
import { calcFieldsToDisplay, DEAL_INDEX, indexNames } from "constants/reports";
import { useCharacteristics, usePrompt } from "hooks";
import { debounce, difference } from "lodash";
import { useAccountSelector, useDealActions, useMeasuresSelector } from "store";
import type { CardConfig, ReportTableColumn, TableCardConfig } from "store/types";
import { normalizeGroupByCharacteristic } from "utils";

import { SectionLabel } from "./SectionLabel";

const MAX_TABLE_COLUMNS = 8;

// Columns are identified by their `key` when present (duplicates always get a unique key),
// otherwise by `aggregation` (the original, single instance of a measure/characteristic).
const sameColumnInstance = (col: ReportTableColumn, target?: ReportTableColumn) => {
  if (!target) return false;
  return target.key ? col.key === target.key : col.aggregation === target.aggregation && !col.key;
};

interface Props {
  config: TableCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
}

export const TableSettings: React.FC<Props> = ({ config, onUpdateConfig }) => {
  const { getMeasuresAction } = useDealActions();
  const measures = useMeasuresSelector();
  const { support_investor_groups } = useAccountSelector() || {};
  const characteristicsAll = useCharacteristics();
  const openPrompt = usePrompt();

  const [loadingMeasures, setLoadingMeasures] = useState(false);
  const [pmeMenuOpen, setPmeMenuOpen] = useState(false);
  const pmeChipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (measures.length === 0) {
      setLoadingMeasures(true);
      getMeasuresAction().finally(() => setLoadingMeasures(false));
    }
  }, [measures.length, getMeasuresAction]);

  const tableColumns = useMemo(
    () => ({
      measures: config.measures || [],
      characteristics: config.characteristics || [],
      calcFields: config.calculatedFields || []
    }),
    [config.measures, config.characteristics, config.calculatedFields]
  );
  const rawGroupBy = useMemo(() => config.rawGroupBy || ["none", "none"], [config.rawGroupBy]);
  const rowBy: "none" | "deal" | "investor" | "measure" = config.rowBy ? config.rowBy : "none";
  const indexNameValue = config.indexName;

  const selectedUniqueParameters = useMemo(
    () => [
      ...difference(tableColumns.calcFields, calcFieldsToDisplay),
      ...tableColumns.characteristics,
      ...tableColumns.measures
    ],
    [tableColumns]
  );

  const allSelectedColumns = useMemo(
    () => [...tableColumns.measures, ...tableColumns.calcFields, ...tableColumns.characteristics],
    [tableColumns]
  );

  // The table renders one column per instance, so duplicates count toward the limit.
  // Use the total instance count (config.columns) — falling back to the unique selection
  // for configs saved before columns existed — so the counter and every gate agree.
  const totalColumnCount =
    config.columns && config.columns.length > 0 ? config.columns.length : allSelectedColumns.length;
  const isAtMax = totalColumnCount >= MAX_TABLE_COLUMNS;

  const buildTableConfig = useCallback(
    (updates: {
      measures?: string[];
      calcFields?: string[];
      characteristics?: string[];
      groupBy?: string[];
      rowBy?: "deal" | "investor" | "measure" | "none";
      indexName?: string | undefined;
    }) => {
      const nextMeasures = updates.measures ?? tableColumns.measures;
      const nextCalcFields = updates.calcFields ?? tableColumns.calcFields;
      const nextCharacteristics = updates.characteristics ?? tableColumns.characteristics;
      const nextGroupBy = updates.groupBy ?? rawGroupBy;
      const nextRowBy: "none" | "deal" | "investor" | "measure" = updates.rowBy ?? rowBy;
      const nextIndexName = updates.indexName !== undefined ? updates.indexName : indexNameValue;

      const allColumns = [...nextMeasures, ...nextCalcFields, ...nextCharacteristics];
      const selectedAggregations = new Set(allColumns);
      // Preserve existing column instances (their settings, order, and any duplicates) whose
      // aggregation is still selected, then append newly selected aggregations that have no column yet.
      const keptColumns = (config.columns || []).filter((col) => selectedAggregations.has(col.aggregation));
      const keptAggregations = new Set(keptColumns.map((col) => col.aggregation));
      const addedColumns = allColumns
        .filter((aggregation) => !keptAggregations.has(aggregation))
        .map((aggregation) => ({ aggregation }));
      const columnsForApi = [...keptColumns, ...addedColumns];
      const allGroupBy = [
        ...nextGroupBy.filter((g) => g && g !== "none"),
        ...(nextRowBy !== "none" ? [nextRowBy] : [])
      ];

      const updatedConfig = {
        ...config,
        measures: nextMeasures,
        calculatedFields: nextCalcFields,
        characteristics: nextCharacteristics,
        columns: columnsForApi.length > 0 ? columnsForApi : undefined,
        groupBy: allGroupBy.map((g) => [g]),
        rawGroupBy: nextGroupBy,
        columnBy: "measure" as const,
        rowBy: nextRowBy === "none" ? undefined : nextRowBy,
        indexName: nextIndexName || undefined,
        columnWidths: undefined
      };

      onUpdateConfig(updatedConfig);
    },
    [config, tableColumns, rawGroupBy, rowBy, indexNameValue, onUpdateConfig]
  );

  const handleToggleCalcField = (param: string) => {
    if (param === "PME") {
      setPmeMenuOpen(true);
      return;
    }
    const isRemoving = tableColumns.calcFields.includes(param);
    if (!isRemoving && isAtMax) return;
    const nextCalcFields = isRemoving
      ? tableColumns.calcFields.filter((p) => p !== param)
      : [...tableColumns.calcFields, param];
    buildTableConfig({ calcFields: nextCalcFields });
  };

  const debouncedBuildConfig = useMemo(() => debounce(buildTableConfig, 1000), [buildTableConfig]);

  const handleSelectIndex = (selectedIndex: string) => {
    setPmeMenuOpen(false);
    if (selectedIndex === "none") {
      debouncedBuildConfig({
        calcFields: tableColumns.calcFields.filter((p) => p !== "PME"),
        indexName: undefined
      });
    } else if (selectedIndex === DEAL_INDEX) {
      const hasPme = tableColumns.calcFields.includes("PME");
      if (!hasPme && isAtMax) return;
      const nextCalcFields = hasPme ? tableColumns.calcFields : [...tableColumns.calcFields, "PME"];
      debouncedBuildConfig({ calcFields: nextCalcFields, indexName: undefined });
    } else {
      const hasPme = tableColumns.calcFields.includes("PME");
      if (!hasPme && isAtMax) return;
      const nextCalcFields = hasPme ? tableColumns.calcFields : [...tableColumns.calcFields, "PME"];
      debouncedBuildConfig({ calcFields: nextCalcFields, indexName: selectedIndex });
    }
  };

  const handleOpenColumnsPicker = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      const fixedCalcCount = calcFieldsToDisplay.filter((f) => tableColumns.calcFields.includes(f)).length;
      const newColumnsSet = await openPrompt({
        Component: TableColumnsPicker,
        componentType: "popover",
        anchorEl: event.currentTarget,
        props: {
          selectedColumns: {
            ...tableColumns,
            calcFields: difference(tableColumns.calcFields, calcFieldsToDisplay)
          },
          maxColumns: MAX_TABLE_COLUMNS - fixedCalcCount
        }
      });

      if (!newColumnsSet) return;

      const fixedCalcFields = calcFieldsToDisplay.filter((field) => tableColumns.calcFields.includes(field));
      buildTableConfig({
        characteristics: newColumnsSet.characteristics,
        measures: newColumnsSet.measures,
        calcFields: [...fixedCalcFields, ...newColumnsSet.calcFields]
      });
    },
    [openPrompt, tableColumns, buildTableConfig]
  );

  const [columnSettingsState, setColumnSettingsState] = useState<{
    open: boolean;
    isDuplicate: boolean;
    column?: ReportTableColumn;
    anchorEl?: HTMLElement | null;
  }>({ open: false, isDuplicate: false });

  const [columnMenu, setColumnMenu] = useState<{ anchorEl: HTMLElement | null; column?: ReportTableColumn }>({
    anchorEl: null
  });

  // The visible column rows come from config.columns (which can hold duplicate instances of the
  // same aggregation). Fall back to the selected names for configs saved before columns existed.
  const displayColumns = useMemo<ReportTableColumn[]>(
    () =>
      config.columns && config.columns.length > 0
        ? config.columns
        : allSelectedColumns.map((aggregation) => ({ aggregation })),
    [config.columns, allSelectedColumns]
  );

  const resolveCurrentColumns = useCallback(
    (): ReportTableColumn[] =>
      config.columns && config.columns.length > 0
        ? config.columns
        : allSelectedColumns.map((aggregation) => ({ aggregation })),
    [config.columns, allSelectedColumns]
  );

  const openColumnSettings = (column: ReportTableColumn, isDuplicate: boolean) => {
    const anchorEl = columnMenu.anchorEl;
    setColumnMenu({ anchorEl: null });
    setColumnSettingsState({ open: true, column, isDuplicate, anchorEl });
  };

  const handleColumnSettings: ColumnSettingsPopoverProps["onApply"] = useCallback(
    (queries) => {
      const col = columnSettingsState.column;
      const isDuplicate = columnSettingsState.isDuplicate;
      setColumnSettingsState({ open: false, isDuplicate: false });
      if (!col) return;

      const currentColumns = resolveCurrentColumns();
      const settings = queries
        ? {
            abbreviated: queries.abbreviated,
            decimal: queries.decimal,
            dateRange: queries.dateRange,
            currency: queries.currency,
            periodBeginning: queries.periodBeginning
          }
        : {
            abbreviated: undefined,
            decimal: undefined,
            dateRange: undefined,
            currency: undefined,
            periodBeginning: undefined
          };

      if (isDuplicate) {
        // Never let a duplicate push the table past the column limit.
        if (currentColumns.length >= MAX_TABLE_COLUMNS) return;
        const index = currentColumns.findIndex((c) => sameColumnInstance(c, col));
        if (index === -1) return;

        // Build a unique key for the new instance so it stays distinguishable from its siblings.
        const usedKeys = new Set(
          currentColumns.filter((c) => c.aggregation === col.aggregation && c.key).map((c) => c.key)
        );
        let counter = 1;
        let duplicateKey = `${col.aggregation}-dup-${counter}`;
        while (usedKeys.has(duplicateKey)) {
          counter += 1;
          duplicateKey = `${col.aggregation}-dup-${counter}`;
        }

        // Give the original a stable key too, so the two instances never collide on matching.
        const original = currentColumns[index].key
          ? currentColumns[index]
          : { ...currentColumns[index], key: col.aggregation };
        const duplicate: ReportTableColumn = { ...original, key: duplicateKey, ...settings };

        const updatedColumns = [
          ...currentColumns.slice(0, index),
          original,
          duplicate,
          ...currentColumns.slice(index + 1)
        ];
        onUpdateConfig({ ...config, columns: updatedColumns });
        return;
      }

      const updatedColumns = currentColumns.map((c) => (sameColumnInstance(c, col) ? { ...c, ...settings } : c));
      onUpdateConfig({ ...config, columns: updatedColumns });
    },
    [columnSettingsState.column, columnSettingsState.isDuplicate, config, resolveCurrentColumns, onUpdateConfig]
  );

  const handleRemoveColumn = useCallback(
    (param: string) => {
      if (tableColumns.measures.includes(param)) {
        buildTableConfig({ measures: tableColumns.measures.filter((m) => m !== param) });
      } else if (tableColumns.calcFields.includes(param)) {
        buildTableConfig({ calcFields: tableColumns.calcFields.filter((c) => c !== param) });
      } else if (tableColumns.characteristics.includes(param)) {
        buildTableConfig({ characteristics: tableColumns.characteristics.filter((c) => c !== param) });
      }
    },
    [tableColumns, buildTableConfig]
  );

  const handleRemoveColumnInstance = useCallback(
    (col: ReportTableColumn) => {
      const currentColumns = config.columns || [];
      const instanceCount = currentColumns.filter((c) => c.aggregation === col.aggregation).length;
      if (instanceCount > 1) {
        // Removing a duplicate: drop just this instance, keep the aggregation selected.
        onUpdateConfig({ ...config, columns: currentColumns.filter((c) => !sameColumnInstance(c, col)) });
      } else {
        // Last instance of the aggregation: remove it from the selected parameters entirely.
        handleRemoveColumn(col.aggregation);
      }
    },
    [config, onUpdateConfig, handleRemoveColumn]
  );

  const groupByOptions: SelectOption<string>[] = useMemo(
    () => [
      { key: "none", value: "none", text: "None" },
      { key: "investor", value: "investor", text: "Investor" },
      ...(support_investor_groups ? [{ key: "investorGroup", value: "investorGroup", text: "Investor Group" }] : []),
      { key: "deal", value: "deal", text: "Deal" },
      { key: "measure", value: "measure", text: "Measure" },
      { key: "month", value: "month", text: "Month" },
      { key: "quarter", value: "quarter", text: "Quarter" },
      { key: "year", value: "year", text: "Year" },
      ...characteristicsAll.map((char) => {
        const val = typeof char === "string" ? normalizeGroupByCharacteristic(char) : (char as any).value;
        const text = typeof char === "string" ? char : (char as any).text;
        return { key: val, value: val, text };
      })
    ],
    [characteristicsAll, support_investor_groups]
  );

  const getGroupOptions = (currentIndex: number) => {
    const otherValue = currentIndex === 0 ? rawGroupBy[1] : rawGroupBy[0];
    return groupByOptions.filter((opt) => opt.value === "none" || opt.value !== otherValue);
  };

  const rowByOptions: SelectOption<string>[] = useMemo(
    () => [
      { key: "none", value: "none", text: "None" },
      { key: "deal", value: "deal", text: "Deal" },
      { key: "investor", value: "investor", text: "Investor" },
      { key: "measure", value: "measure", text: "Measure" }
    ],
    []
  );

  return (
    <>
      <Box>
        <SectionLabel>Parameters</SectionLabel>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 1 }}>
          {calcFieldsToDisplay.map((param) => {
            const isSelected = tableColumns.calcFields.includes(param);
            const isDisabled = !isSelected && isAtMax;
            return (
              <Chip
                key={param}
                ref={param === "PME" ? pmeChipRef : undefined}
                label={param}
                size="small"
                onClick={() => handleToggleCalcField(param)}
                sx={{
                  fontSize: 11,
                  height: 24,
                  cursor: isDisabled ? "default" : "pointer",
                  bgcolor: isSelected ? "#c3c9cf" : undefined,
                  opacity: isDisabled ? 0.45 : 1,
                  "&:hover": { bgcolor: isDisabled ? undefined : "#e2e8f0" }
                }}
              />
            );
          })}
          <Menu anchorEl={pmeChipRef.current} open={pmeMenuOpen} onClose={() => setPmeMenuOpen(false)}>
            <MenuItem onClick={() => handleSelectIndex("none")} sx={{ fontSize: 12 }}>
              None
            </MenuItem>
            <MenuItem
              selected={!indexNameValue && tableColumns.calcFields.includes("PME")}
              onClick={() => handleSelectIndex(DEAL_INDEX)}
              sx={{ fontSize: 12 }}
            >
              Deal specific index
            </MenuItem>
            {Object.entries(indexNames).map(([index, name]) => (
              <MenuItem
                key={index}
                selected={index === indexNameValue}
                onClick={() => handleSelectIndex(index)}
                sx={{ fontSize: 12 }}
              >
                {name}
              </MenuItem>
            ))}
          </Menu>
          {selectedUniqueParameters.length > 0 && (
            <Chip
              label={`(${selectedUniqueParameters.length})`}
              size="small"
              sx={{ fontSize: 11, height: 24, bgcolor: "#c3c9cf" }}
            />
          )}
        </Box>
        {loadingMeasures ? (
          <CircularProgress size={16} />
        ) : (
          <Button
            size="sm"
            onClick={handleOpenColumnsPicker}
            disabled={isAtMax}
            sx={{ fontSize: 11, textTransform: "none", color: isAtMax ? "#9CA3AF" : "#26A6AE", p: "2px 6px" }}
          >
            More columns...
          </Button>
        )}
      </Box>

      {allSelectedColumns.length > 0 && (
        <Box>
          <SectionLabel>
            Selected Parameters ({totalColumnCount}/{MAX_TABLE_COLUMNS})
          </SectionLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {displayColumns.map((col) => (
              <Box
                key={col.key || col.aggregation}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: "2px",
                  px: "4px",
                  borderRadius: "4px",
                  "&:hover": { bgcolor: "#f5f5f5" }
                }}
              >
                <Typography sx={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {col.aggregation}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  <IconButton
                    size="sm"
                    onClick={(e) => setColumnMenu({ anchorEl: e.currentTarget, column: col })}
                    sx={{ p: "2px" }}
                  >
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="sm" onClick={() => handleRemoveColumnInstance(col)} sx={{ p: "2px" }}>
                    <CloseIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
          <Menu
            anchorEl={columnMenu.anchorEl}
            open={Boolean(columnMenu.anchorEl)}
            onClose={() => setColumnMenu({ anchorEl: null })}
          >
            <MenuItem
              onClick={() => columnMenu.column && openColumnSettings(columnMenu.column, false)}
              sx={{ fontSize: 12 }}
            >
              <ListItemIcon>
                <TuneIcon color="primary" sx={{ fontSize: 14 }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Column Settings</ListItemText>
            </MenuItem>
            <MenuItem
              disabled={isAtMax}
              onClick={() => !isAtMax && columnMenu.column && openColumnSettings(columnMenu.column, true)}
              sx={{ fontSize: 12 }}
            >
              <ListItemIcon>
                <ContentCopyIcon color={isAtMax ? "disabled" : "primary"} sx={{ fontSize: 14 }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Duplicate Column</ListItemText>
            </MenuItem>
          </Menu>
          <ColumnSettingsPopover
            open={columnSettingsState.open}
            anchorEl={columnSettingsState.anchorEl}
            column={columnSettingsState.column}
            isDuplicate={columnSettingsState.isDuplicate}
            hideSetDefault
            onClose={() => setColumnSettingsState({ open: false, isDuplicate: false })}
            onApply={handleColumnSettings}
          />
        </Box>
      )}

      <Box>
        <SectionLabel>Group By</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <AutocompleteMultiSelect
            key={`group1-${rawGroupBy[0]}`}
            defaultValue={getGroupOptions(0).find((o) => o.value === rawGroupBy[0])}
            options={getGroupOptions(0)}
            label="Group 1"
            selectAll={false}
            onChange={(value) => buildTableConfig({ groupBy: [(value as string) || "none", rawGroupBy[1]] })}
          />
          <AutocompleteMultiSelect
            key={`group2-${rawGroupBy[1]}`}
            defaultValue={getGroupOptions(1).find((o) => o.value === rawGroupBy[1])}
            options={getGroupOptions(1)}
            label="Group 2"
            selectAll={false}
            onChange={(value) => buildTableConfig({ groupBy: [rawGroupBy[0], (value as string) || "none"] })}
          />
        </Box>
      </Box>

      <Box>
        <SectionLabel>Last Group (Row By)</SectionLabel>
        <AutocompleteMultiSelect
          key={`rowBy-${rowBy}`}
          defaultValue={rowByOptions.find((o) => o.value === rowBy)}
          options={rowByOptions}
          label="Last Group"
          selectAll={false}
          onChange={(value) =>
            buildTableConfig({
              rowBy: (value as "deal" | "investor" | "measure" | "none") || "none"
            })
          }
        />
      </Box>
    </>
  );
};
